import React, { useState } from 'react';
import { X, Save, User, Mail, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { User as UserType, UserRole } from '@/types';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';

interface UserEditModalProps {
  user: UserType | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

const UserEditModal: React.FC<UserEditModalProps> = ({
  user,
  isOpen,
  onClose,
  onUpdate
}) => {
  const [formData, setFormData] = useState({
    role: user?.role || UserRole.USER,
    isActive: user?.isActive ?? true
  });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) {
      setFormData({
        role: user.role,
        isActive: user.isActive ?? true
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      await apiService.put(`/users/${user.id}`, formData);
      toast.success('Benutzer erfolgreich aktualisiert');
      onUpdate();
      onClose();
    } catch (error: any) {
      console.error('Error updating user:', error);
      const errorMessage = error.response?.data?.message || 'Fehler beim Aktualisieren des Benutzers';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Benutzer bearbeiten</h2>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Azure AD Info */}
          {user.azureId && (
            <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Shield className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Azure AD Benutzer
                </span>
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-300">
                Name und E-Mail werden automatisch aus Azure AD synchronisiert und können nicht bearbeitet werden.
              </p>
            </div>
          )}

          {/* Name (Read-only from Azure AD) */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Name (Azure AD)</Label>
            <div className="flex items-center space-x-2 p-3 bg-muted rounded-md">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{user.name}</span>
            </div>
          </div>

          {/* Email (Read-only from Azure AD) */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">E-Mail (Azure AD)</Label>
            <div className="flex items-center space-x-2 p-3 bg-muted rounded-md">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{user.email}</span>
            </div>
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role">Rolle</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => handleInputChange('role', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Rolle auswählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UserRole.USER}>Benutzer</SelectItem>
                <SelectItem value={UserRole.VIEWER}>Betrachter</SelectItem>
                <SelectItem value={UserRole.ADMIN}>Administrator</SelectItem>
                <SelectItem value={UserRole.SUPERADMIN}>Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Active Status */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="isActive">Aktiv</Label>
              <p className="text-sm text-muted-foreground">
                Benutzer kann sich anmelden und das System nutzen
              </p>
            </div>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => handleInputChange('isActive', checked)}
            />
          </div>

          {/* Azure ID (Read-only) */}
          {user.azureId && (
            <div className="space-y-2 pt-4 border-t">
              <Label className="text-sm text-muted-foreground">Azure ID</Label>
              <div className="p-3 bg-muted rounded-md">
                <span className="text-sm font-mono text-xs break-all">{user.azureId}</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Speichern
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditModal;
